/**
 * Attack Surface Cell Utilities
 *
 * Provides data transformation logic for the AttackSurfaceCell component,
 * specifically handling attack surface value prioritization based on active filters.
 *
 * @module attackSurfaceCell.util
 * @see AttackSurfaceCell.tsx - Component that uses this utility
 */

/**
 * Prioritizes attack surface values based on active table filters
 *
 * Priority Rules:
 * 1. No filters: Alphabetical order
 * 2. With filters: Filtered values first (alphabetical), then others (alphabetical)
 *
 * @param attackSurfaceValues - Attack surface values to prioritize
 * @param activeFilters - Currently selected attack surface filters from table state
 * @returns Prioritized and sorted array of attack surface values
 *
 * @example
 * ```typescript
 * // No filters - alphabetical
 * getPrioritizedAttackSurface(['Internal', 'External'])
 * // => ['External', 'Internal']
 *
 * // With filters - filtered values first
 * getPrioritizedAttackSurface(['Internal', 'External', 'DMZ'], ['internal'])
 * // => ['Internal', 'DMZ', 'External']
 * ```
 */
export const getPrioritizedAttackSurface = (
  attackSurfaceValues: readonly string[],
  activeFilters?: string[]
): string[] => {
  if (!attackSurfaceValues.length) return [];

  // No filters active: Alphabetical order
  if (!activeFilters || activeFilters.length === 0) {
    return [...attackSurfaceValues].sort();
  }

  // Filters active: Normalize filters and split into tiers
  const normalizedFilters = activeFilters.map(filter => filter.toLowerCase());
  const filteredSet = new Set(normalizedFilters);

  const matchedValues = attackSurfaceValues
    .filter(value => filteredSet.has(value.toLowerCase()))
    .sort();

  const unmatchedValues = attackSurfaceValues
    .filter(value => !filteredSet.has(value.toLowerCase()))
    .sort();

  return [...matchedValues, ...unmatchedValues];
};
